import { FC, useContext, useEffect, useMemo, useState } from 'react'
import './ChannelCanvas.scss'
import { ChannelCanvasData, useChannelCanvasData } from './hooks/useChannelCanvasData'
import { CutterContext } from '../contexts/CutterContext'
import { lerp } from './functions/lerp'

interface Props {
  index: number
}

const canvasWidth = 1000
export const channelCanvasHeight = 128

export const ChannelCanvas: FC<Props> = ({ index }) => {
  const [canvasElement, setCanvasElement] = useState<HTMLCanvasElement | null>(null)
  const { visibleRange, audioBuffer } = useContext(CutterContext)
  const source = useMemo(() => audioBuffer.getChannelData(index), [audioBuffer, index])
  const channelData = useChannelCanvasData({ source, canvasWidth })

  useEffect(() => {
    if (canvasElement && channelData) {
      const pixelRatio = window.devicePixelRatio
      const canvasContext = canvasElement.getContext('2d') as CanvasRenderingContext2D
      canvasContext.fillStyle = '#2C3035'
      canvasContext.clearRect(0, 0, canvasWidth * pixelRatio, channelCanvasHeight * pixelRatio)
      for (let pixelIndex = 0; pixelIndex < canvasWidth; pixelIndex++) {
        const height = getPixelHeight(channelData, pixelIndex)
        canvasContext.fillRect(pixelIndex * pixelRatio, channelCanvasHeight - height / 2, pixelRatio, height)
      }
    }
  }, [canvasElement, channelData, visibleRange])

  return (
    <canvas
      className="ChannelCanvas"
      ref={setCanvasElement}
      width={canvasWidth * window.devicePixelRatio}
      height={channelCanvasHeight * window.devicePixelRatio}
      style={{ width: canvasWidth, height: channelCanvasHeight }}
    />
  )
}

function getPixelHeight({ data, offset, scaleFactor, samplesPerPixel }: ChannelCanvasData, pixelIndex: number) {
  const sampleIndex = offset + pixelIndex * scaleFactor
  const currentSampleIndex = Math.floor(sampleIndex)
  if (samplesPerPixel < 1) {
    return data[currentSampleIndex] * channelCanvasHeight * 2
  }
  const nextSampleIndex = Math.ceil(sampleIndex)
  const currentSample = data[currentSampleIndex]
  const nextSample = data[nextSampleIndex]
  return lerp(currentSample, nextSample, sampleIndex - currentSampleIndex) * channelCanvasHeight * 2
}
